/**
 * 系統管理 → AI 設定。
 *
 * ## 這一頁在管什麼
 *
 * 「內容會不會被送出岸上、送到哪一家、用誰的金鑰」。目前只有景點文字的機器翻譯
 * 用得到，之後 C-3 語音導覽與 ASR 詞表會各自成為一列——所以畫面是逐功能一張卡片，
 * 而不是一組全域設定。
 *
 * ## 金鑰只進不出
 *
 * 畫面上永遠只看得到末四碼。要換就整把重貼，沒有「顯示目前金鑰」——那個功能唯一
 * 的用途是外洩。清空欄位再儲存等於移除金鑰。
 *
 * ## 三種「改了卻沒生效」都要講出來
 *
 *   - 被環境變數覆寫：部署層的 `BOAT_*` 蓋過資料庫，畫面上的值不會生效
 *   - 沒有加密金鑰：本環境未設 `BOAT_SECRET_ENC_KEY`，API 金鑰存不進資料庫
 *   - 功能關閉：設定填得再完整，翻譯按鈕仍會回「尚未啟用」
 *
 * 少講任何一項，使用者就會對著一個看起來正常的表單找半天。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import {
  listAiSettings,
  testAiSettings,
  updateAiSettings,
  type AiSettings,
  type AiSettingsTestResult,
} from '../lib/api-client';

const PROVIDERS: Array<{ value: string; label: string }> = [
  { value: 'openai', label: 'OpenAI（相容端點）' },
  { value: 'stub', label: 'stub（測試用假回應，不連外）' },
];

export function AiSettingsPage(): JSX.Element {
  const query = useQuery({ queryKey: ['ai-settings'], queryFn: () => listAiSettings() });

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">AI 設定</h1>
      </div>

      <p className="page-intro">
        各項 AI 功能的供應商、模型與金鑰。這裡的設定<strong>立即生效，不需要重啟 API</strong>；
        金鑰以 AES-256-GCM 加密後存進資料庫，畫面上只顯示末四碼。
      </p>

      <div
        style={{
          background: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: 6,
          padding: 12,
          margin: '12px 0',
          fontSize: 13,
          maxWidth: 900,
        }}
      >
        ⚠ 啟用任何一項功能，等於讓對應的內容<strong>離開岸上、送到外部服務</strong>。
        開通前請確認該用途已取得核可——C-3 語音模組那道契約閘管的是乘客語音，不涵蓋這裡。
        每次呼叫都會寫稽核紀錄（誰、哪個景點、哪些欄位、哪個模型、送出幾個字）。
      </div>

      {query.isLoading && <div>載入中…</div>}
      {query.error && <div className="error-banner">{(query.error as Error).message}</div>}

      {query.data?.items.map((item) => (
        <FeatureCard key={item.feature} settings={item} />
      ))}
    </div>
  );
}

/** 表單狀態；`provider` 用 string 而非產生型別的字面聯集——下拉的值本來就是字串。 */
interface FeatureForm {
  enabled: boolean;
  provider: string;
  model: string;
  base_url: string;
  timeout_ms: string;
}

function FeatureCard({ settings }: { settings: AiSettings }): JSX.Element {
  const qc = useQueryClient();
  const [form, setForm] = useState<FeatureForm>({
    enabled: settings.enabled,
    provider: settings.provider,
    model: settings.model,
    base_url: settings.base_url,
    timeout_ms: String(settings.timeout_ms),
  });
  /** null = 沒有要動金鑰；空字串 = 要清掉。 */
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [test, setTest] = useState<AiSettingsTestResult | null>(null);

  // 伺服器回來的值是這張卡片的真相；重新整理或別人改過之後要跟上
  useEffect(() => {
    setForm({
      enabled: settings.enabled,
      provider: settings.provider,
      model: settings.model,
      base_url: settings.base_url,
      timeout_ms: String(settings.timeout_ms),
    });
    setApiKey(null);
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      updateAiSettings(settings.feature, {
        enabled: form.enabled,
        provider: form.provider,
        model: form.model,
        base_url: form.base_url,
        timeout_ms: Number(form.timeout_ms),
        ...(apiKey === null ? {} : { api_key: apiKey }),
      }),
    onSuccess: () => {
      setSaved(true);
      setTest(null);
      void qc.invalidateQueries({ queryKey: ['ai-settings'] });
    },
  });

  const runTest = useMutation({
    mutationFn: () => testAiSettings(settings.feature),
    onSuccess: (r) => setTest(r),
  });

  const keyState =
    settings.api_key_source === 'db'
      ? `已設定（${settings.api_key_hint ?? '…'}）`
      : settings.api_key_source === 'env'
        ? '由環境變數提供'
        : '未設定';

  return (
    <section
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        padding: 16,
        margin: '12px 0',
        maxWidth: 900,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>{settings.label}</h2>
        <code style={{ fontSize: 11, color: '#9ca3af' }}>{settings.feature}</code>
        {settings.enabled ? <Pill tone="success">已啟用</Pill> : <Pill>已關閉</Pill>}
      </div>

      {settings.env_override && (
        <div className="error-banner" style={{ margin: '8px 0' }}>
          此功能目前被<strong>環境變數覆寫</strong>（部署層的 <code>BOAT_TRANSLATE_*</code>
          ）。下面的設定不會生效——要改成由這一頁管理，請先移除該環境變數並重啟 API。
        </div>
      )}
      {!settings.encryption_available && (
        <div className="error-banner" style={{ margin: '8px 0' }}>
          本環境未設定 <code>BOAT_SECRET_ENC_KEY</code>，<strong>金鑰無法存進資料庫</strong>。
          請先設定一把 32 位元組金鑰（base64 或 hex）並重啟 API；其餘欄位仍可儲存。
        </div>
      )}

      <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
          />
          啟用這個功能
        </label>

        <Field label="供應商">
          <select
            value={form.provider}
            onChange={(e) => setForm((p) => ({ ...p, provider: e.target.value }))}
            style={{ padding: 8, maxWidth: 320 }}
          >
            {PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="模型" hint="例如 gpt-4o-mini（便宜、翻譯夠用）、gpt-4o（品質較好）">
          <input
            type="text"
            value={form.model}
            onChange={(e) => setForm((p) => ({ ...p, model: e.target.value }))}
            style={{ padding: 8, width: 320 }}
          />
        </Field>

        <Field label="API 端點" hint="OpenAI 相容端點；自架或代理服務也填這裡">
          <input
            type="text"
            value={form.base_url}
            onChange={(e) => setForm((p) => ({ ...p, base_url: e.target.value }))}
            style={{ padding: 8, width: 420 }}
          />
        </Field>

        <Field label="逾時（毫秒）" hint="1000～600000；翻譯一個景點通常幾秒內完成">
          <input
            type="number"
            value={form.timeout_ms}
            onChange={(e) => setForm((p) => ({ ...p, timeout_ms: e.target.value }))}
            style={{ padding: 8, width: 160 }}
          />
        </Field>

        <Field label="API 金鑰" hint={`目前狀態：${keyState}。留空不動；要移除請按「清除金鑰」`}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="password"
              value={apiKey ?? ''}
              placeholder={apiKey === '' ? '（儲存後移除金鑰）' : '貼上新的金鑰以更換'}
              onChange={(e) => setApiKey(e.target.value === '' ? null : e.target.value)}
              autoComplete="new-password"
              style={{ padding: 8, width: 420 }}
            />
            {settings.api_key_source === 'db' && (
              <Button variant="ghost" onClick={() => setApiKey('')}>
                清除金鑰
              </Button>
            )}
            {apiKey !== null && (
              <span style={{ fontSize: 12, color: '#b45309' }}>
                {apiKey === '' ? '儲存後會移除金鑰' : '儲存後會更換金鑰'}
              </span>
            )}
          </div>
        </Field>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
        <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? '儲存中…' : '儲存設定'}
        </Button>
        <Button variant="secondary" onClick={() => runTest.mutate()} disabled={runTest.isPending}>
          {runTest.isPending ? '測試中…' : '測試連線'}
        </Button>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>
          最後更新 {new Date(settings.updated_at).toLocaleString('zh-TW')}
        </span>
      </div>

      {saved && !save.isPending && !save.error && (
        <div style={{ fontSize: 12, color: '#047857', marginTop: 8 }}>✓ 已儲存</div>
      )}
      {save.error && (
        <div className="error-banner" style={{ marginTop: 8 }}>
          {(save.error as Error).message}
        </div>
      )}
      {runTest.error && (
        <div className="error-banner" style={{ marginTop: 8 }}>
          {(runTest.error as Error).message}
        </div>
      )}
      {test && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            fontSize: 12,
            borderRadius: 4,
            background: test.ok ? '#ecfdf5' : '#fffbeb',
            border: `1px solid ${test.ok ? '#6ee7b7' : '#fcd34d'}`,
          }}
        >
          {test.ok ? (
            <>
              ✓ 連線正常：{test.provider} / {test.model}，往返 {test.latency_ms} ms
              {test.total_tokens !== null && `，用了 ${test.total_tokens} tokens`}。 回覆：
              <code>{test.reply}</code>
            </>
          ) : (
            <>⚠ {test.reply}</>
          )}
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13 }}>
      <span>{label}</span>
      {children}
      {hint && <small style={{ color: '#9ca3af' }}>{hint}</small>}
    </label>
  );
}
